<template>
  <div class="pt-2 sm:p-4 " v-if="detailedRecentEvents.length > 0">
    <div>
      <div class="flex items-start mb-4">
        <div class="grow">
          <h3 class="text-md font-semibold text-gray-800">Actividad reciente</h3>
        </div>
        <button
          v-if="dismissedEventsCount > 0"
          @click="$emit('clear-dismissed-events')"
          class="text-xs text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0"
          title="Mostrar todos los eventos eliminados"
        >
          Mostrar eliminados
        </button>
      </div>

      <div class="space-y-3 mb-4">
        <!-- Tarjeta expandida para solicitudes de coliving -->
        <div
          v-for="event in detailedRecentEvents"
          :key="event.id"
          :class="[
            'p-3 rounded-lg border group transition-all duration-200',
            (event.type === 'coliving_application' && event.applicationData) || 
            (event.type === 'coliving_pre_approved' && event.applicationData) || 
            (event.type === 'coliving_assignment' && event.applicationData)
              ? 'bg-white border-gray-200 shadow-sm hover:shadow-md cursor-pointer' 
              : 'bg-gray-50 border-gray-200'
          ]"
        >
          <!-- Solicitud de coliving expandida -->
          <div 
            v-if="['coliving_application', 'coliving_pre_approved', 'coliving_payment', 'coliving_assignment'].includes(event.type) && event.applicationData" 
            class="space-y-2"
          >
            <!-- Header con icono y título -->
            <div class="flex items-start justify-between">
              <div class="flex items-start gap-2 flex-1 min-w-0">
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-gray-900 break-words">{{ getEventTitle(event) }}</p>
                  <p class="text-xs text-gray-500">{{ formatEventDate(event.date) }}</p>
                </div>
              </div>
              <button
                @click.stop="$emit('dismiss-event', event.id)"
                class="flex-shrink-0 w-4 h-4 rounded-full bg-gray-200 hover:bg-red-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
                :title="$t('common.deleteEvent')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5 text-gray-500 hover:text-red-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <!-- Información del solicitante -->
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <h4 class="text-sm font-medium text-indigo-900 break-words">{{ event.applicationData.applicantName }}</h4>
                <div class="flex items-center space-x-2 flex-shrink-0">
                    <div class="relative group">
                  <ShieldCheckIcon 
                        class="h-4 w-4 cursor-pointer"
                        :class="[getUserVerificationStatus(event.applicationData.applicantId).hasCompletedProfile ? 'text-green-500' : 'text-gray-400']"
                  />
                      <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                        {{ getUserVerificationStatus(event.applicationData.applicantId).hasCompletedProfile ? 'Identidad verificada' : 'Identidad pendiente' }}
                      </div>
                    </div>
                    <div class="relative group">
                  <DocumentTextIcon 
                        class="h-4 w-4 cursor-pointer"
                        :class="[getUserVerificationStatus(event.applicationData.applicantId).hasEssentialDocuments ? 'text-green-500' : 'text-gray-400']"
                  />
                      <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                        {{ getUserVerificationStatus(event.applicationData.applicantId).hasEssentialDocuments ? 'Documentación completa' : 'Documentación pendiente' }}
                      </div>
                    </div>
                    <div class="relative group">
                  <BanknotesIcon 
                        class="h-4 w-4 cursor-pointer"
                        :class="[getUserVerificationStatus(event.applicationData.applicantId).hasGuarantor ? 'text-green-500' : 'text-gray-400']"
                  />
                      <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                        {{ getUserVerificationStatus(event.applicationData.applicantId).guarantorInfo?.fullName ? `Con avalista: ${getUserVerificationStatus(event.applicationData.applicantId).guarantorInfo.fullName}` : getUserVerificationStatus(event.applicationData.applicantId).hasGuarantor ? 'Avalista pendiente' : 'Sin avalista' }}
                      </div>
                    </div>
                </div>
              </div>
              
              <!-- Información de la aplicación -->
              <div class="flex flex-wrap gap-2 text-xs pt-2">
                <span v-if="event.applicationData.assignedRoomData" class="inline-flex items-center px-2 py-1 rounded-md bg-indigo-50 text-indigo-800 font-medium">
                  {{ event.applicationData.assignedRoomData.title || `Hab. ${event.applicationData.assignedRoomData.number}` }}
                </span>
                <span class="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-700">
                  {{ getStayReasonText(event.applicationData.stayReason) }}
                </span>
                <span v-if="event.applicationData.assignedRoomData?.price" class="inline-flex items-center px-2 py-1 rounded-md bg-green-50 text-green-800 font-bold">
                  {{ formatCurrency(event.applicationData.assignedRoomData.price) }}
                </span>
                  </div>
              
              <!-- Barra de período de estancia -->
              <div v-if="event.applicationData.desiredStartDate && event.applicationData.desiredEndDate" class="pt-2">
                <StayPeriodBar 
                  :start-date="event.applicationData.desiredStartDate"
                  :end-date="event.applicationData.desiredEndDate"
                  :show-period-info="true"
                  color-variant="indigo"
                />
              </div>
            </div>

            <!-- Mensaje informativo según el estado -->
            <div class="pt-1">
              <p class="text-xs font-medium text-left break-words" :class="getStatusInfoClass(event.applicationData.status)">
                {{ getStatusInfoText(event.applicationData) }}
              </p>
            </div>
          </div>

          <!-- Solicitud de coliving pre-asignada expandida -->
          <div 
            v-else-if="event.type === 'coliving_pre_approved' && event.applicationData" 
            class="space-y-2 cursor-pointer"
            @click="$emit('view-coliving-application', event.applicationData)"
          >
            <!-- Header con icono y título -->
            <div class="flex items-start justify-between">
              <div class="flex items-start gap-2 flex-1 min-w-0">
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-gray-900 break-words">{{ event.title }}</p>
                  <p class="text-xs text-gray-500">{{ formatEventDate(event.date) }}</p>
                </div>
              </div>
              <button
                @click.stop="$emit('dismiss-event', event.id)"
                class="flex-shrink-0 w-4 h-4 rounded-full bg-gray-200 hover:bg-red-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
                :title="$t('common.deleteEvent')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5 text-gray-500 hover:text-red-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <!-- Información del solicitante -->
            <div class="space-y-2">
                            <div class="flex items-center justify-between">
                <h4 class="text-sm font-medium text-cyan-900 break-words">{{ event.applicationData.applicantName }}</h4>
                <div class="flex items-center space-x-2 flex-shrink-0">
                  <!-- Indicadores de estado del perfil con iconos -->
                  <div class="relative group">
                  <ShieldCheckIcon 
                        class="h-4 w-4 cursor-pointer"
                        :class="[getUserVerificationStatus(event.applicationData.applicantId).hasCompletedProfile ? 'text-green-500' : 'text-gray-400']"
                    />
                    <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                        {{ getUserVerificationStatus(event.applicationData.applicantId).hasCompletedProfile ? 'Identidad verificada' : 'Identidad pendiente' }}
                    </div>
                  </div>
                  <div class="relative group">
                  <DocumentTextIcon 
                        class="h-4 w-4 cursor-pointer"
                        :class="[getUserVerificationStatus(event.applicationData.applicantId).hasEssentialDocuments ? 'text-green-500' : 'text-gray-400']"
                    />
                    <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                        {{ getUserVerificationStatus(event.applicationData.applicantId).hasEssentialDocuments ? 'Documentación completa' : 'Documentación pendiente' }}
                    </div>
                  </div>
                  <div class="relative group">
                  <BanknotesIcon 
                        class="h-4 w-4 cursor-pointer"
                        :class="[getUserVerificationStatus(event.applicationData.applicantId).hasGuarantor ? 'text-green-500' : 'text-gray-400']"
                    />
                    <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                        {{ getUserVerificationStatus(event.applicationData.applicantId).guarantorInfo?.fullName ? `Con avalista: ${getUserVerificationStatus(event.applicationData.applicantId).guarantorInfo.fullName}` : getUserVerificationStatus(event.applicationData.applicantId).hasGuarantor ? 'Avalista pendiente' : 'Sin avalista' }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Información de la aplicación -->
              <div class="flex flex-wrap gap-2 text-xs pt-2">
                <span v-if="event.applicationData.assignedRoomData" class="inline-flex items-center px-2 py-1 rounded-md bg-cyan-50 text-cyan-800 font-medium">
                  {{ event.applicationData.assignedRoomData.title || `Hab. ${event.applicationData.assignedRoomData.number}` }}
                </span>
                <span class="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-700">
                  {{ getStayReasonText(event.applicationData.stayReason) }}
                </span>
                <span v-if="event.applicationData.assignedRoomData?.price" class="inline-flex items-center px-2 py-1 rounded-md bg-green-50 text-green-800 font-bold">
                  {{ formatCurrency(event.applicationData.assignedRoomData.price) }}
                </span>
              </div>
              
              <!-- Barra de período de estancia -->
              <div v-if="event.applicationData.desiredStartDate && event.applicationData.desiredEndDate" class="pt-2">
                <StayPeriodBar 
                  :start-date="event.applicationData.desiredStartDate"
                  :end-date="event.applicationData.desiredEndDate"
                  :show-period-info="true"
                  color-variant="blue"
                />
              </div>
            </div>
            <!-- Mensaje informativo para pre-asignados -->
            <div class="pt-1">
              <p class="text-xs text-blue-800 font-medium text-left break-words">
                {{ event.applicationData.applicantName }} debe firmar y hacer el primer pago
              </p>
            </div>
          </div>

          <!-- Solicitud de coliving con pago completado expandida -->
          <div 
            v-else-if="event.type === 'coliving_payment' && event.applicationData" 
            class="space-y-2 cursor-pointer"
            @click="$emit('view-coliving-application', event.applicationData)"
          >
            <!-- Header con icono y título -->
            <div class="flex items-start justify-between">
              <div class="flex items-start gap-2 flex-1 min-w-0">
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-gray-900 break-words">{{ event.title }}</p>
                  <p class="text-xs text-gray-500">{{ formatEventDate(event.date) }}</p>
                </div>
              </div>
              <button
                @click.stop="$emit('dismiss-event', event.id)"
                class="flex-shrink-0 w-4 h-4 rounded-full bg-gray-200 hover:bg-red-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
                :title="$t('common.deleteEvent')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5 text-gray-500 hover:text-red-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <!-- Información del solicitante -->
            <div class="space-y-2">
                            <div class="flex items-center justify-between">
                <h4 class="text-sm font-medium text-emerald-900 break-words">{{ event.applicationData.applicantName }}</h4>
                <div class="flex items-center space-x-2 flex-shrink-0">
                  <!-- Indicadores de estado del perfil con iconos -->
                  <div class="relative group">
                  <ShieldCheckIcon 
                        class="h-4 w-4 cursor-pointer"
                        :class="[getUserVerificationStatus(event.applicationData.applicantId).hasCompletedProfile ? 'text-green-500' : 'text-gray-400']"
                  />
                    <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                        {{ getUserVerificationStatus(event.applicationData.applicantId).hasCompletedProfile ? 'Identidad verificada' : 'Identidad pendiente' }}
                    </div>
                  </div>
                  <div class="relative group">
                  <DocumentTextIcon 
                        class="h-4 w-4 cursor-pointer"
                        :class="[getUserVerificationStatus(event.applicationData.applicantId).hasEssentialDocuments ? 'text-green-500' : 'text-gray-400']"
                  />
                    <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                        {{ getUserVerificationStatus(event.applicationData.applicantId).hasEssentialDocuments ? 'Documentación completa' : 'Documentación pendiente' }}
                    </div>
                  </div>
                  <div class="relative group">
                  <BanknotesIcon 
                        class="h-4 w-4 cursor-pointer"
                        :class="[getUserVerificationStatus(event.applicationData.applicantId).hasGuarantor ? 'text-green-500' : 'text-gray-400']"
                  />
                    <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                        {{ getUserVerificationStatus(event.applicationData.applicantId).guarantorInfo?.fullName ? `Con avalista: ${getUserVerificationStatus(event.applicationData.applicantId).guarantorInfo.fullName}` : getUserVerificationStatus(event.applicationData.applicantId).hasGuarantor ? 'Avalista pendiente' : 'Sin avalista' }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Información de la aplicación -->
              <div class="flex flex-wrap gap-2 text-xs pt-2">
                <span v-if="event.applicationData.assignedRoomData" class="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-800 font-medium">
                  {{ event.applicationData.assignedRoomData.title || `Hab. ${event.applicationData.assignedRoomData.number}` }}
                </span>
                <span class="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-700">
                  {{ getStayReasonText(event.applicationData.stayReason) }}
                </span>
                <span v-if="event.applicationData.assignedRoomData?.price" class="inline-flex items-center px-2 py-1 rounded-md bg-green-50 text-green-800 font-bold">
                  {{ formatCurrency(event.applicationData.assignedRoomData.price) }}
                </span>
              </div>
              
              <!-- Barra de período de estancia -->
              <div v-if="event.applicationData.desiredStartDate && event.applicationData.desiredEndDate" class="pt-2">
                <StayPeriodBar 
                  :start-date="event.applicationData.desiredStartDate"
                  :end-date="event.applicationData.desiredEndDate"
                  :show-period-info="true"
                  color-variant="green"
                />
              </div>
            </div>
            <!-- Mensaje informativo para pagos -->
            <div class="pt-1">
              <p class="text-xs text-green-800 font-medium text-left break-words">
                Falta confirmar la asignación final
              </p>
            </div>
          </div>

          <!-- Solicitud de coliving asignada (habitación confirmada) expandida -->
          <div 
            v-else-if="event.type === 'coliving_assignment' && event.applicationData" 
            class="space-y-2 cursor-pointer"
            @click="$emit('view-coliving-application', event.applicationData)"
          >
            <!-- Header con icono y título -->
            <div class="flex items-start justify-between">
              <div class="flex items-start gap-2 flex-1 min-w-0">
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-gray-900 break-words">{{ event.title }}</p>
                  <p class="text-xs text-gray-500">{{ formatEventDate(event.date) }}</p>
                </div>
              </div>
              <button
                @click.stop="$emit('dismiss-event', event.id)"
                class="flex-shrink-0 w-4 h-4 rounded-full bg-gray-200 hover:bg-red-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
                :title="$t('common.deleteEvent')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5 text-gray-500 hover:text-red-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <!-- Información del solicitante -->
            <div class="space-y-2">
                            <div class="flex items-center justify-between">
                <h4 class="text-sm font-medium text-teal-900 break-words">{{ event.applicationData.applicantName }}</h4>
                <div class="flex items-center space-x-2 flex-shrink-0">
                  <!-- Indicadores de estado del perfil con iconos -->
                  <div class="relative group">
                  <ShieldCheckIcon 
                        class="h-4 w-4 cursor-pointer"
                        :class="[getUserVerificationStatus(event.applicationData.applicantId).hasCompletedProfile ? 'text-green-500' : 'text-gray-400']"
                    />
                    <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                        {{ getUserVerificationStatus(event.applicationData.applicantId).hasCompletedProfile ? 'Identidad verificada' : 'Identidad pendiente' }}
                    </div>
                  </div>
                  <div class="relative group">
                  <DocumentTextIcon 
                        class="h-4 w-4 cursor-pointer"
                        :class="[getUserVerificationStatus(event.applicationData.applicantId).hasEssentialDocuments ? 'text-green-500' : 'text-gray-400']"
                    />
                    <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                        {{ getUserVerificationStatus(event.applicationData.applicantId).hasEssentialDocuments ? 'Documentación completa' : 'Documentación pendiente' }}
                    </div>
                  </div>
                  <div class="relative group">
                  <BanknotesIcon 
                        class="h-4 w-4 cursor-pointer"
                        :class="[getUserVerificationStatus(event.applicationData.applicantId).hasGuarantor ? 'text-green-500' : 'text-gray-400']"
                    />
                    <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                        {{ getUserVerificationStatus(event.applicationData.applicantId).guarantorInfo?.fullName ? `Con avalista: ${getUserVerificationStatus(event.applicationData.applicantId).guarantorInfo.fullName}` : getUserVerificationStatus(event.applicationData.applicantId).hasGuarantor ? 'Avalista pendiente' : 'Sin avalista' }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Información de la aplicación -->
              <div class="flex flex-wrap gap-2 text-xs pt-2">
                <span v-if="event.applicationData.assignedRoomData" class="inline-flex items-center px-2 py-1 rounded-md bg-purple-50 text-purple-800 font-medium">
                  {{ event.applicationData.assignedRoomData.title || `Hab. ${event.applicationData.assignedRoomData.number}` }}
                </span>
                <span class="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-700">
                  {{ getStayReasonText(event.applicationData.stayReason) }}
                </span>
                <span v-if="event.applicationData.assignedRoomData?.price" class="inline-flex items-center px-2 py-1 rounded-md bg-green-50 text-green-800 font-bold">
                  {{ formatCurrency(event.applicationData.assignedRoomData.price) }}
                </span>
              </div>
              
              <!-- Barra de período de estancia -->
              <div v-if="event.applicationData.desiredStartDate && event.applicationData.desiredEndDate" class="pt-2">
                <StayPeriodBar 
                  :start-date="event.applicationData.desiredStartDate"
                  :end-date="event.applicationData.desiredEndDate"
                  :show-period-info="true"
                  color-variant="purple"
                />
              </div>
            </div>

            <!-- Mensaje informativo para asignados -->
            <div class="pt-1">
              <p class="text-xs text-green-800 font-medium text-left break-words">
                {{ event.applicationData.applicantName }} tiene habitación confirmada
              </p>
            </div>
          </div>

          <!-- Evento estándar (no es solicitud de coliving) -->
          <div v-else class="flex items-start gap-2">
            <div 
              :class="getEventIconClass(event.type)"
              class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <!-- Eventos de contratos tradicionales -->
                <path v-if="event.type === 'signed'" fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                <path v-else-if="event.type === 'interest'" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
                <path v-else-if="event.type === 'assigned'" d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                <path v-else-if="event.type === 'contract'" fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                
                <!-- Eventos de colivings generales -->
                <path v-else-if="event.type === 'coliving'" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                
                <!-- Eventos específicos de aplicaciones de colivings -->
                <path v-else-if="event.type === 'coliving_application'" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                <path v-else-if="event.type === 'coliving_pre_approved'" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path v-else-if="event.type === 'coliving_signed'" fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                <path v-else-if="event.type === 'coliving_payment'" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
                <path v-else-if="event.type === 'coliving_assignment'" d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.84L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                <path v-else-if="event.type === 'coliving_rejected'" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" />
                <path v-else-if="event.type === 'coliving_terminated'" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" />
                <path v-else-if="event.type === 'coliving_application_retired'" d="M9 12l2 2 4-4m-6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                
                <!-- Otros eventos -->
                <path v-else-if="event.type === 'verification'" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path v-else fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs font-medium text-gray-900">{{ event.title }}</p>
              <p class="text-xs text-gray-500 truncate">{{ event.description }}</p>
              <p class="text-xs text-gray-400 mt-1">{{ formatEventDate(event.date) }}</p>
            </div>
            <button
              @click="$emit('dismiss-event', event.id)"
              class="flex-shrink-0 w-4 h-4 rounded-full bg-gray-200 hover:bg-red-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
              title="Eliminar evento"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5 text-gray-500 hover:text-red-600" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- <button 
        @click="$emit('go-to-activity')" 
        class="w-full py-2 px-4 text-sm font-medium text-center text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-100 transition-colors"
      >
        Ver toda la actividad
      </button> -->
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { BanknotesIcon, DocumentTextIcon, ShieldCheckIcon } from '@heroicons/vue/24/solid'
import StayPeriodBar from '../StayPeriodBar.vue'

const { t } = useI18n()

const props = defineProps({
  detailedRecentEvents: {
    type: Array,
    default: () => []
  },
  dismissedEventsCount: {
    type: Number,
    default: 0
  },
  userVerificationData: {
    type: Map,
    default: () => new Map()
  }
})

const emit = defineEmits([
  'clear-dismissed-events',
  'dismiss-event',
  'go-to-activity',
  'view-coliving-application'
])

const formatCurrency = (amount) => {
  if (!amount) return ''
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0
  }).format(amount)
}

const getEventIconClass = (eventType) => {
  const classes = {
    contract: 'bg-green-100 text-green-600',
    signed: 'bg-green-100 text-green-600',
    assigned: 'bg-blue-100 text-blue-600',
    interest: 'bg-blue-100 text-blue-600',
    coliving: 'bg-purple-100 text-purple-600',
    coliving_application: 'bg-indigo-100 text-indigo-600',
    coliving_pre_approved: 'bg-cyan-100 text-cyan-600',
    coliving_signed: 'bg-green-100 text-green-600',
    coliving_payment: 'bg-emerald-100 text-emerald-600',
    coliving_assignment: 'bg-teal-100 text-teal-600',
    coliving_rejected: 'bg-red-100 text-red-600',
    coliving_terminated: 'bg-gray-100 text-gray-600',
    coliving_application_retired: 'bg-orange-100 text-orange-600',
    verification: 'bg-yellow-100 text-yellow-600',
    default: 'bg-gray-100 text-gray-600'
  }
  return classes[eventType] || classes.default
}

const formatEventDate = (date) => {
  if (!date) return ''
  try {
    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) return ''
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj)
  } catch (error) {
    console.warn('Error al formatear fecha:', date, error)
    return ''
  }
}

const getEventTitle = (event) => {
  if (!event || !event.applicationData) return event.title || 'Evento'
  const status = event.applicationData.status
  const colivingTitle = event.applicationData.colivingTitle || event.title || 'Coliving'
  const titleMap = {
    'submitted': `Nueva solicitud - ${colivingTitle}`,
    'pre_approved': `Solicitud pre-aprobada - ${colivingTitle}`,
    'owner_signed': `Contrato para firmar - ${colivingTitle}`,
    'applicant_signed': `Contrato para firmar - ${colivingTitle}`,
    'contract_signed': `Contrato firmado - ${colivingTitle}`,
    'payment_completed': `Pago completado - ${colivingTitle}`,
    'room_assigned': `Asignación confirmada - ${colivingTitle}`
  }
  return titleMap[status] || event.title
}

const getStatusInfoText = (applicationData) => {
  if (!applicationData) return ''
  const applicantName = applicationData.applicantName || t('common.theTenant')
  const status = applicationData.status
  const infoMap = {
    'submitted': 'Revisa la solicitud para pre-aprobarla',
    'pre_approved': `${applicantName} debe revisar y firmar el contrato`,
    'owner_signed': `Esperando firma de ${applicantName}`,
    'applicant_signed': 'Falta tu firma para completar el contrato',
    'contract_signed': `${applicantName} debe realizar el primer pago`,
    'payment_completed': 'Pago completado, falta confirmar la asignación final',
    'room_assigned': `${applicantName} tiene habitación confirmada`
  }
  return infoMap[status] || t('common.reviewStatus')
}

const getStatusInfoClass = (status) => {
  const classMap = {
    'submitted': 'text-blue-800',
    'pre_approved': 'text-blue-800',
    'owner_signed': 'text-cyan-800',
    'applicant_signed': 'text-orange-800',
    'contract_signed': 'text-purple-800',
    'payment_completed': 'text-emerald-800',
    'room_assigned': 'text-green-800 font-bold'
  }
  return classMap[status] || 'text-gray-800'
}

const getUserVerificationStatus = (userId) => {
  if (!userId || !props.userVerificationData) {
    return { 
      hasCompletedProfile: false, 
      hasEssentialDocuments: false, 
      hasGuarantor: false,
      guarantorInfo: null
    }
  }
  return props.userVerificationData.get(userId) || { 
    hasCompletedProfile: false, 
    hasEssentialDocuments: false, 
    hasGuarantor: false,
    guarantorInfo: null
  }
}

const getStayReasonText = (stayReason) => {
  const reasonMap = {
    'work': 'Trabajo',
    'study': 'Estudio', 
    'leisure': 'Ocio',
    'other': 'Otro'
  }
  return reasonMap[stayReason] || stayReason || t('common.notSpecified')
}

const formatApplicationDate = (date) => {
  if (!date) return t('common.dateNotSpecified')
  try {
    const dateObj = date.toDate ? date.toDate() : new Date(date)
    if (isNaN(dateObj.getTime())) return t('common.invalidDate')
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(dateObj)
  } catch (error) {
    console.warn('Error al formatear fecha de aplicación:', date, error)
    return t('common.invalidDate')
  }
}
</script> 